import React, { useState, useEffect } from "react";
import {
  Avatar,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  Favorite,
  FavoriteBorder,
  MoreVert,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Reply,
} from "@mui/icons-material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { ReplyItem } from "./ReplyItem";
import { getTimeAgo } from "./utils";
import ShortcutOutlinedIcon from "@mui/icons-material/ShortcutOutlined";
import { CommentType } from "./IcommentType";

export const CommentItem: React.FC<{
  comment: CommentType;
  onEdit: (id: number, text: string) => void;
  onDeleteComment: (id: number) => void;
  onDeleteReply: (id: number) => void;
  onAddReply: (parentId: number, text: string) => void;
  onToggleReplyInput: (id: number) => void;
  onToggleReplies: (id: number) => void;
  replyInputOpen: boolean;
  showReplies: boolean;
  replyText: string;
  onReplyTextChange: (id: number, text: string) => void;
  editingCommentId: number | null;
  editCommentText: string;
  onEditCommentTextChange: (text: string) => void;
  onSaveEditedComment: () => void;
  onCancelEdit: () => void;
  anchorEl: HTMLElement | null;
  onMenuClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMenuClose: () => void;
  onEditClick: () => void;
  onLikeComment: (commentId: number) => void;
  onLikeReply?: (commentId: number) => void;
  menuComment?: CommentType | null;
}> = ({
  comment,
  onEdit,
  onDeleteComment,
  onDeleteReply,
  onAddReply,
  onToggleReplyInput,
  onToggleReplies,
  replyInputOpen,
  showReplies,
  replyText,
  onReplyTextChange,
  editingCommentId,
  editCommentText,
  onEditCommentTextChange,
  onSaveEditedComment,
  onCancelEdit,
  anchorEl,
  onMenuClick,
  onMenuClose,
  onEditClick,
  onLikeComment,
  onLikeReply = onLikeComment,
  menuComment,
}) => {
  const [timeAgo, setTimeAgo] = useState(
    getTimeAgo(new Date(comment.created_on))
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeAgo(getTimeAgo(new Date(comment.created_on)));
    }, 60000);

    return () => clearInterval(interval);
  }, [comment.created_on]);

  // Check if this comment's menu is currently open
  const isMenuOpen = Boolean(anchorEl) && menuComment?.id === comment.id;

  return (
    <Box
      sx={{
        mb: 3,
        pl: 2,
        "& .MuiButton-root": {
          minWidth: "auto",
        },
        "& .MuiIconButton-root": {
          padding: "4px",
        },
      }}
    >
      <Box display="flex" gap={2}>
        <Avatar
          sx={{
            bgcolor: comment.is_user_commented ? "#1976d2" : "#ccc",
            width: 32,
            height: 32,
            // color: comment.is_user_commented ? "#ffff" : "#0000",
            fontSize: "16px",
            lineHeight: "24px",
            fontWeight: "400",
          }}
        >
          {comment.alice_name?.toLocaleUpperCase()}
        </Avatar>
        <Box flexGrow={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontWeight="500" fontSize="14px" lineHeight={"20px"}>
              {comment.is_user_commented === true
                ? "You"
                : `${comment.student_name}`}
            </Typography>
            <Typography
              color="text.secondary"
              fontSize="12px"
              lineHeight={"16px"}
            >
              {timeAgo}
            </Typography>
            {comment.is_user_commented === true && (
              <>
                <IconButton
                  size="small"
                  onClick={onMenuClick}
                  sx={{ ml: "auto" }}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={isMenuOpen}
                  onClose={onMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  <MenuItem onClick={onEditClick}>Edit</MenuItem>
                  <MenuItem
                    onClick={() => {
                      onDeleteComment(comment.id);
                    }}
                  >
                    Delete
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>

          {editingCommentId === comment.id ? (
            <Box mt={1}>
              <TextField
                fullWidth
                multiline
                value={editCommentText}
                onChange={(e) => onEditCommentTextChange(e.target.value)}
                size="small"
                minRows={2}
                placeholder="Edit your comment..."
              />
              <Box mt={1} display="flex" gap={1}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={onSaveEditedComment}
                  className="edit-post-save"
                  disabled={!editCommentText.trim()}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  onClick={onCancelEdit}
                  className="edit-post-cancel"
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography
              mt={1}
              sx={{
                whiteSpace: "pre-wrap",
                fontSize: "14px",
                lineHeight: "20px",
                color: "#374151",
              }}
            >
              {comment.comment}
            </Typography>
          )}

          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <Typography
              className="like-button-main"
              onClick={() => onLikeComment(comment.id)}
            >
              <IconButton
                size="small"
                // onClick={() =>}
                sx={{
                  color: comment.is_user_liked ? "#ef4444":"text.secondary",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
                aria-label="like"
                className="like-icon"
              >
                {comment.is_user_liked ? (
                  <Favorite fontSize="small" />
                ) : (
                  <FavoriteBorder fontSize="small" />
                )}
              </IconButton>
              <Typography
                variant="caption"
                color={comment.likes_count === 0 ? "text.secondary" : "#ef4444"}
                fontSize={"14px"}
                className="like-button"
              >
                {comment.likes_count}
              </Typography>
            </Typography>

            <Button
              size="small"
              startIcon={
                <ShortcutOutlinedIcon
                  fontSize="small"
                  style={{ transform: "scalex(-1)" }}
                />
              }
              onClick={() => onToggleReplyInput(comment.id)}
              className="reply-button"
            >
              <p>Reply</p>
            </Button>

            {comment.children.length > 0 && (
              <Button
                size="small"
                // endIcon={
                //   showReplies ? <KeyboardArrowUp /> : <KeyboardArrowDown />
                // }
                onClick={() => onToggleReplies(comment.id)}
                sx={{ fontSize: "14px" }}
                className="show-reply"
              >
                <p>
                  {showReplies
                    ? "Hide Replies"
                    : `Show Replies (${comment.children.length})`}
                </p>
              </Button>
            )}
          </Box>

          {replyInputOpen && (
            <Box mt={1}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                placeholder="Write a reply..."
                value={replyText}
                onChange={(e) => onReplyTextChange(comment.id, e.target.value)}
              />
              <Box mt={1} display="flex" gap={1}>
                <Button
                  size="small"
                  // variant="contained"
                  className="post-replay"
                  onClick={() => onAddReply(comment.id, replyText)}
                  disabled={!replyText.trim()}
                  sx={{
                    "&.Mui-disabled": {
                      background: "#7faaff",
                      color: "#ffff",
                    },
                  }}
                >
                  Reply
                </Button>
                <Button
                  size="small"
                  className="post-cancel"
                  onClick={() => onToggleReplyInput(comment.id)}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}

          <Collapse in={showReplies}>
            <Box sx={{ mt: 2, ml: 1 }}>
              {comment.children.map((reply) => (
                <ReplyItem
                  key={reply.id}
                  reply={reply}
                  onAddReply={onAddReply}
                  onEditReply={onEdit}
                  onDeleteReply={onDeleteReply}
                  onLikeReply={onLikeReply}
                />
              ))}
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
};
